import { type FormEvent, useCallback, useState } from 'react';
import { type Organization, useForceAssignToOrg, useOrganizations } from '../../hooks/useAdminData';
import { Modal } from '../Modal';
import { SearchSelect } from '../SearchSelect';

interface ForceAssignModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

export function ForceAssignModal({ open, onClose, userId, userName }: ForceAssignModalProps) {
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [role, setRole] = useState('member');
  const [orgSearch, setOrgSearch] = useState('');

  const forceAssign = useForceAssignToOrg();
  const { data: orgsData, isLoading } = useOrganizations(
    orgSearch ? { search: orgSearch, limit: 10 } : { limit: 10 },
  );

  const handleOrgSearch = useCallback((query: string) => {
    setOrgSearch(query);
  }, []);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selectedOrg) return;

    forceAssign.mutate(
      { userId, data: { organizationId: selectedOrg.id, role } },
      {
        onSuccess: () => {
          setSelectedOrg(null);
          setRole('member');
          onClose();
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Assign to Organization">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-text-secondary">
          Assign <span className="text-text font-medium">{userName}</span> to an organization
        </p>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Organization</label>
          <SearchSelect<Organization>
            placeholder="Search organizations..."
            onSearch={handleOrgSearch}
            options={orgsData?.data ?? []}
            loading={isLoading}
            selected={selectedOrg}
            onSelect={setSelectedOrg}
            renderOption={(org) => <div className="text-text text-sm">{org.name}</div>}
            renderSelected={(org) => <span>{org.name}</span>}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!selectedOrg || forceAssign.isPending}
            className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {forceAssign.isPending ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
